import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shield, Printer, Download, Loader2, FileText } from 'lucide-react';
import InsuranceAdminNotice from '@/components/InsuranceAdminNotice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRAM_LABELS = {
  '': 'All programs',
  SS: 'SelleShare',
  SF: 'SelleFair',
  SD: 'SelleDeclare',
  SW: 'SelleWarranty',
  SWP: 'SelleWarranty Plus',
  USPSP: 'USPS Protect',
  A: 'Parcel Risk A',
  B: 'Parcel Risk B',
  C: 'Parcel Risk C',
};

// Mike, 25-oct: the daily report is same day, and he prints today's shipments
// to send them to the insurer. "today" is the San Diego day (the backend decides it).
const TODAY = 'today';

const queryParams = (days, programCode) => {
  const params = new URLSearchParams(days === TODAY ? { day: TODAY } : { days });
  if (programCode) params.set('program_code', programCode);
  return params;
};

export default function BrokerReportPage() {
  const { token, user } = useAuth();
  const [days, setDays] = useState(TODAY);
  const [programCode, setProgramCode] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = queryParams(days, programCode);
      const r = await fetch(`${API}/insurance/broker/ledger?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setData(d);
    } finally { setLoading(false); }
  }, [days, programCode, token]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    const params = queryParams(days, programCode);
    const r = await fetch(`${API}/insurance/broker/ledger.csv?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = days === TODAY ? `insurance_ledger_${data?.day || 'today'}.csv` : `insurance_ledger_${days}d.csv`;
    a.click();
  };

  const windowLabel = days === TODAY
    ? `today, ${data?.day || ''} (Pacific time)`
    : `last ${days} days`;

  const totals = data?.records
    ? data.records.reduce((acc, r) => {
        acc[r.program_code || 'unknown'] = (acc[r.program_code || 'unknown'] || 0) + Number(r.pure_premium || 0);
        return acc;
      }, {})
    : {};

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="broker-report-page">
      <InsuranceAdminNotice />

      {/* Printable header */}
      <div className="flex items-start justify-between gap-4 flex-wrap print:mb-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-500 print:hidden" /> Insurance Broker Report
          </h2>
          <p className="text-slate-500 mt-1 text-sm">
            Pure premium ledger · {data?.count || 0} authorizations · <span data-testid="report-window">{windowLabel}</span>
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            Viewer: {user?.email} · Generated {new Date().toLocaleString()}
          </p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-btn">
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
          <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="csv-btn">
            <Download className="w-4 h-4 mr-1.5" /> CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="print:hidden">
        <CardContent className="pt-4 grid sm:grid-cols-3 gap-3">
          <div>
            <Label>Period</Label>
            <Select value={String(days)} onValueChange={(v) => setDays(v === TODAY ? TODAY : Number(v))}>
              <SelectTrigger data-testid="days-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={TODAY}>Today (Pacific time)</SelectItem>
                {[7, 14, 30, 60, 90, 180, 365].map((d) => <SelectItem key={d} value={String(d)}>Last {d} days</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Program</Label>
            <Select value={programCode || 'ALL'} onValueChange={(v) => setProgramCode(v === 'ALL' ? '' : v)}>
              <SelectTrigger data-testid="program-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All programs</SelectItem>
                {Object.entries(PROGRAM_LABELS).filter(([k]) => k).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Totals */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Stat label="Total pure premium" value={`$${(data?.total_pure_premium || 0).toFixed(2)}`} testid="stat-premium" />
        <Stat label="Total declared value" value={`$${(data?.total_declared_value || 0).toFixed(2)}`} testid="stat-declared" />
        <Stat label="Authorizations" value={data?.count || 0} testid="stat-count" />
      </div>

      {/* Program breakdown */}
      {Object.keys(totals).length > 1 && (
        <Card className="print:break-inside-avoid">
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Premium by program</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500">
                <tr><th className="text-left px-2 py-1">Program</th><th className="text-right px-2 py-1">Pure premium</th></tr>
              </thead>
              <tbody>
                {Object.entries(totals).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <tr key={k} className="border-t border-slate-100">
                    <td className="px-2 py-1">{PROGRAM_LABELS[k] || k}</td>
                    <td className="px-2 py-1 text-right tabular-nums">${v.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Ledger table */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Authorization ledger</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
          ) : data?.records?.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">
              {days === TODAY ? 'No insurance authorizations today yet.' : 'No insurance authorizations in this window.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900/60 text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-2 py-2">Authorization #</th>
                    <th className="text-left px-2 py-2">Merchant</th>
                    <th className="text-left px-2 py-2">Date &amp; time</th>
                    <th className="text-left px-2 py-2">Tracking #</th>
                    <th className="text-left px-2 py-2">Program</th>
                    <th className="text-left px-2 py-2">Commodity</th>
                    <th className="text-left px-2 py-2">From → To</th>
                    <th className="text-right px-2 py-2">Declared</th>
                    <th className="text-right px-2 py-2">Pure premium</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.records?.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`ledger-row-${r.id.slice(0, 8)}`}>
                      <td className="px-2 py-2 font-mono">{r.authorization_number}</td>
                      <td className="px-2 py-2">{r.merchant || '—'}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{new Date(r.timestamp).toLocaleString()}</td>
                      <td className="px-2 py-2 font-mono">{r.tracking_number || '—'}</td>
                      <td className="px-2 py-2">{PROGRAM_LABELS[r.program_code] || r.program_code || '—'}</td>
                      <td className="px-2 py-2">{r.commodity || '—'}</td>
                      <td className="px-2 py-2">{r.from_zip || '—'} → {r.to_zip || '—'}</td>
                      <td className="px-2 py-2 text-right tabular-nums">${Number(r.declared_value || 0).toFixed(2)}</td>
                      <td className="px-2 py-2 text-right tabular-nums font-semibold">${Number(r.pure_premium || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400 print:mt-8">
        SelleSend Insurance Broker Report · confidential.
      </p>
    </div>
  );
}

const Stat = ({ label, value, testid }) => (
  <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4" data-testid={testid}>
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-2xl font-bold tabular-nums mt-1">{value}</p>
  </div>
);
